import React, { useEffect, useRef } from "react";
import Navbar from '../Components/Navbar';
import { isMobile } from "react-device-detect";
import GLightbox from 'glightbox';
import 'glightbox/dist/css/glightbox.css';
import Footer from "../Components/Footer";
import Typed from "typed.js";
import { motion } from "motion/react";
import Core from "../Components/Core";

const Alumni = () => {
  const typedEl = useRef(null);

  useEffect(() => {
    const typed = new Typed(typedEl.current, {
      strings: [
        "Welcome to Ebun Heritage Alumni Network",
        "Celebrating Our Graduates and Their Achievements",
        "Once Ebun, Always Ebun",
      ],
      typeSpeed: 50,
      backSpeed: 30,
      backDelay: 2000,
      loop: true,
      showCursor: true,
      cursorChar: "<span class='text-warning'>|</span>",
    });

    return () => {
      typed.destroy();
    };
  }, []);

  return (
    <div>
      <Navbar />

      {/* HERO SECTION */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
        className="bg-white pt-lg-6 pt-4 pb-lg-6 pb-4 px-lg-6 px-4"
        style={{
          backgroundImage: "url('../../assets/images/background/slide/head4.jpg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "80% 20%",
          opacity: 0.95,
        }}
      >
        <div className="container">
          <div className="row align-items-center py-lg-10 py-8 px-lg-0">
            <div className="col-lg-12 text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="fs-1 fw-semibold text-dark mb-3"
              >
                <span ref={typedEl}></span>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ALUMNI OVERVIEW SECTION */}
      <section className="bg-white pt-lg-6 pt-4 pb-lg-8 pb-8 px-lg-6 px-4">
        <div className="container">
          <div className="row gx-8 py-lg-6 px-lg-0">

            {/* Alumni Network */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6"
            >
              <div className="fs-t2 text-gray-800 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Our Alumni Network
              </div>

              <div className={`fs-4 text-dark ${isMobile ? '' : 'text-justify'}`}>
                The Ebun Heritage Alumni Network is a vibrant community of former students 
                who continue to embody the values of excellence, integrity, discipline, 
                and faith instilled during their time at the school.
                <br /><br />
                Our alumni are making remarkable impacts across various fields including 
                medicine, law, engineering, business, academia, technology, and public service.
                They remain ambassadors of the moral and academic foundation built at Ebun Heritage Schools.
              </div>
            </motion.div>

            {/* Why Alumni Matters */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6 mt-lg-0 mt-6"
            >
              <div className="fs-t2 text-gray-800 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Why Our Alumni Matter
              </div>

              <div className={`fs-4 text-dark ${isMobile ? '' : 'text-justify'}`}>
                Our graduates are a testament to our commitment to nurturing God-given potentials.
                Their achievements inspire current students to dream bigger and strive harder.
                <br /><br />
                We encourage lifelong connection, mentorship, networking opportunities,
                and collaboration among alumni and current students to strengthen our community.
              </div>
            </motion.div>

            {/* Alumni Engagement */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-12 mt-lg-8 mt-6"
            >
              <div className="fs-t2 text-gray-700 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Stay Connected
              </div>

              <div className={`fs-4 text-dark ${isMobile ? 'px-4' : 'text-justify'}`}>
                We invite all graduates of Ebun Heritage Schools to stay connected
                through reunions, alumni events, mentorship programs, and community outreach initiatives.
                <br /><br />
                ✔ Alumni Reunions <br />
                ✔ Career Networking Events <br />
                ✔ Mentorship Opportunities <br />
                ✔ Community Development Projects
                <br /><br />
                Once you are part of Ebun Heritage, you remain part of our legacy forever.
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ACHIEVEMENT HIGHLIGHT SECTION */}
      <section className="bg-back pt-lg-6 pt-8 pb-lg-6 pb-4 px-lg-6 px-0">
        <div className="container">

          {/* LEGACY SECTION */}
          <div className="row align-items-center pt-lg-0 pt-6 pb-lg-0 px-lg-0">

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6 mb-6 mb-lg-0"
            >
              <div className="fs-t2 text-gray-800 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Our Everlasting Legacy
              </div>

              <div className={`fs-4 text-dark ${isMobile ? 'px-4' : 'text-justify'}`}>
                Since our establishment in September 2000, Ebun Heritage Schools 
                has consistently produced graduates who uphold our core values.
                <br /><br />
                Our alumni remain living proof that when God-given potential 
                is nurtured in a disciplined and supportive environment, 
                greatness becomes inevitable.
                <br /><br />
                We celebrate every alumnus and alumna as a vital part of our heritage and future.
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6 text-center"
            >
              <img
                src="../../assets/images/background/his.svg"
                alt="Ebun Heritage Alumni"
                width={500}
                className="img-fluid mb-lg-4"
              />
            </motion.div>

          </div>
        </div>
      </section>

      <Core />
      <Footer />
    </div>
  );
};

export default Alumni;
